<script setup>
import { RouterView, useRoute } from 'vue-router'
import { computed, onMounted, ref } from 'vue'
import AppNavigation from './components/AppNavigation.vue'
import AppLoading from './components/AppLoading.vue'
import AppFooter from './components/AppFooter.vue'
import VerifyEmailAlert from './components/VerifyEmailAlert.vue'
import NoticeDialog from './components/NoticeDialog.vue'
import { useMainStore } from '@/stores/main'
import * as Auth from '@/services/Auth'
import { Config } from '@/Config'

const store = useMainStore()
const route = useRoute()
const initialLoading = ref(true)
const envVars = import.meta.env

// The track editor is a full-screen tool and brings its own menu bar: on that route the site's own
// bar and footer are hidden, and the editor owns the whole viewport (see TrackEditorView).
const isTrackEditor = computed(() => route.name === 'track_editor')

// Initialize some store property.
store.setProperty('envVars', envVars)
store.setProperty('clientConfig', Config)

// Initialize user and language on app mount
// Load the bootstrap data (tracks, ...) before showing the app: a single call.
onMounted(async () => {
  // The user kept in local storage can be out of date — the account may have been activated by
  // following the link in the email — and what the app shows about the email verification comes
  // from that copy: refresh it while the bootstrap request is on its way.
  Auth.refreshUser()

  await store.loadBootstrap()
  initialLoading.value = false
})
</script>

<template>
  <v-app>
    <!-- Loading Screen -->
    <AppLoading :show="initialLoading" />

    <!-- Main App Content -->
    <v-fade-transition>
      <div v-if="!initialLoading" class="app-content">
        <AppNavigation v-if="!isTrackEditor" />
        <v-main>
          <VerifyEmailAlert />
          <!-- Shown once per account, wherever the play button appears (see services/Notice.js) -->
          <NoticeDialog />
          <RouterView />
        </v-main>
        <AppFooter v-if="!isTrackEditor" />
      </div>
    </v-fade-transition>
  </v-app>
</template>

<style scoped>
/* dvh, not vh: on a phone 100vh is the height of the viewport with the address bar hidden, so the
   page would always be a little taller than what you see and scrollable even with nothing to scroll
   (which is what made the scroll feel wrong on mobile). */
.app-content {
  min-height: 100dvh;
}

/* Fade transition for main app content */
.v-fade-transition-enter-active,
.v-fade-transition-leave-active {
  transition: opacity 0.5s ease-in-out;
}

.v-fade-transition-enter-from,
.v-fade-transition-leave-to {
  opacity: 0;
}
</style>


